// Drag-to-resize helpers shared by the app's resizable panes (conversation
// list, manual-actions sidebar, assistant chat). Widths persist per key in
// localStorage. Extracted from App.jsx so panes living in their own modules can
// resize the same way instead of each rolling its own.

import { useState, useCallback } from 'react'
import { Box } from '@mui/material'

export const PANE_WIDTH_KEY_PREFIX = 'cxhub.paneWidth.'

// invert: true when the resizable pane sits to the LEFT of the mouse-drag
// origin's opposite side — i.e. when dragging the handle further from the
// pane should still grow it (the pane is on the right of its handle, so
// moving the handle left grows it — the opposite of the default case where
// the pane is on the left of its handle and moving right grows it).
export function useResizableWidth(key, defaultWidth, { min = 160, max = 800, invert = false } = {}) {
  const [width, setWidth] = useState(() => {
    const stored = Number(localStorage.getItem(PANE_WIDTH_KEY_PREFIX + key))
    return Number.isFinite(stored) && stored > 0 ? Math.min(max, Math.max(min, stored)) : defaultWidth
  })

  const startDrag = useCallback((e) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = width
    function onMove(moveEvent) {
      const delta = moveEvent.clientX - startX
      setWidth(Math.min(max, Math.max(min, startWidth + (invert ? -delta : delta))))
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      // Read the live value at drag-end rather than closing over startWidth.
      setWidth((w) => { localStorage.setItem(PANE_WIDTH_KEY_PREFIX + key, String(w)); return w })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [width, min, max, key, invert])

  return [width, startDrag]
}

// Thin draggable strip between two resizable columns: an 8 px target with a
// visible 3 px grip line that darkens on hover (2026-10-19, so every pane's
// handle is easy to find), and the label as its tooltip.
export function ResizeHandle({ onMouseDown, label }) {
  return (
    <Box
      onMouseDown={onMouseDown}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      title={label}
      data-testid="resize-handle"
      sx={{
        width: 8,
        flexShrink: 0,
        cursor: 'col-resize',
        display: 'flex',
        justifyContent: 'center',
        '&::before': { content: '""', width: 3, height: '100%', bgcolor: 'divider', borderRadius: 1, transition: 'background-color 120ms' },
        '&:hover::before': { bgcolor: 'primary.main' },
      }}
    />
  )
}
